/* global Blob, Worker */
/**
 * Lightweight canvas-based confetti burst.
 * No external dependencies — creates a temporary canvas overlay, fires
 * colourful particles, then removes itself once the animation completes.
 */

interface Particle {
  x: number; y: number; vx: number; vy: number;
  size: number; color: string; gravity: number; drag: number;
  alpha: number; decay: number; rotation: number; rotSpeed: number;
}

/** Viewport coordinates captured before an async action replaces its button. */
export interface ConfettiOrigin { x: number; y: number; }

const COLORS = [
  '#22c55e', '#4ade80', '#86efac',
  '#facc15', '#fbbf24', '#fde047',
  '#38bdf8', '#818cf8', '#f472b6',
];

function makeParticle(cx: number, cy: number): Particle {
  const angle = Math.random() * Math.PI * 2;
  const speed = 4 + Math.random() * 8;

  return {
    x: cx, y: cy,
    vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - 4,
    size: 4 + Math.random() * 4,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    gravity: 0.12, drag: 0.98, alpha: 1,
    decay: 0.012 + Math.random() * 0.008,
    rotation: Math.random() * 360, rotSpeed: (Math.random() - 0.5) * 12,
  };
}

function stepParticle(p: Particle): void {
  p.vy += p.gravity; p.vx *= p.drag; p.vy *= p.drag;
  p.x += p.vx; p.y += p.vy; p.alpha -= p.decay; p.rotation += p.rotSpeed;
}

function drawParticle(ctx: CanvasRenderingContext2D, p: Particle): void {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate((p.rotation * Math.PI) / 180);
  ctx.globalAlpha = Math.max(0, p.alpha);
  ctx.fillStyle = p.color;
  ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
  ctx.restore();
}

function createCanvas(): HTMLCanvasElement {
  const c = document.createElement('canvas');

  c.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;pointer-events:none;z-index:99999';
  c.width = window.innerWidth;
  c.height = window.innerHeight;
  document.body.appendChild(c);

  return c;
}

function resolveOrigin(
  canvas: { width: number; height: number }, target?: HTMLElement | ConfettiOrigin,
): [number, number] {
  if (!target) return [canvas.width / 2, canvas.height / 2];
  if ('x' in target && 'y' in target) return [target.x, target.y];

  const r = target.getBoundingClientRect();

  return [r.left + r.width / 2, r.top + r.height / 2];
}

/* ── One animation for every burst ───────────────────────────────────
   Bursts that overlap share one canvas and one frame loop, so several
   claims landing together still read as a single celebration. The loop
   runs in a worker on an OffscreenCanvas when the browser allows it, so
   a busy main thread (a timeline re-rendering after a claim) cannot
   make the confetti stutter. */

interface MainThreadBurst { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; particles: Particle[] }
interface WorkerBurst { canvas: HTMLCanvasElement; worker: Worker }

let mainThreadBurst: MainThreadBurst | null = null;
let workerBurst: WorkerBurst | null = null;

function drawFrame({ ctx, canvas, particles }: MainThreadBurst): Particle[] {
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const alive = particles.filter((p) => p.alpha > 0);

  alive.forEach((p) => { stepParticle(p); drawParticle(ctx, p); });

  return alive;
}

function animate(burst: MainThreadBurst): void {
  burst.particles = drawFrame(burst);

  if (burst.particles.length > 0) {
    window.requestAnimationFrame(() => animate(burst));
  } else {
    burst.canvas.remove();
    if (mainThreadBurst === burst) mainThreadBurst = null;
  }
}

function startMainThreadBurst(canvas: HTMLCanvasElement, particles: Particle[]): void {
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    canvas.remove();

    return;
  }
  const burst: MainThreadBurst = { canvas, ctx, particles };

  mainThreadBurst = burst;
  window.requestAnimationFrame(() => animate(burst));
}

function fireOnMainThread(cx: number, cy: number, canvas: HTMLCanvasElement): void {
  const particles = Array.from({ length: 80 }, () => makeParticle(cx, cy));

  if (mainThreadBurst) mainThreadBurst.particles.push(...particles);
  else startMainThreadBurst(canvas, particles);
}

// The worker repeats the particle maths above in plain script form: it has no
// module scope to import from, and the two must stay in step.
const WORKER_SOURCE = `
const COLORS = ${JSON.stringify(COLORS)};
let canvas = null, ctx = null, particles = [], running = false;
const make = (cx, cy) => {
  const angle = Math.random() * Math.PI * 2, speed = 4 + Math.random() * 8;
  return { x: cx, y: cy, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - 4,
    size: 4 + Math.random() * 4, color: COLORS[Math.floor(Math.random() * COLORS.length)],
    gravity: 0.12, drag: 0.98, alpha: 1, decay: 0.012 + Math.random() * 0.008,
    rotation: Math.random() * 360, rotSpeed: (Math.random() - 0.5) * 12 };
};
const step = (p) => { p.vy += p.gravity; p.vx *= p.drag; p.vy *= p.drag;
  p.x += p.vx; p.y += p.vy; p.alpha -= p.decay; p.rotation += p.rotSpeed; };
const draw = (p) => { ctx.save(); ctx.translate(p.x, p.y); ctx.rotate((p.rotation * Math.PI) / 180);
  ctx.globalAlpha = Math.max(0, p.alpha); ctx.fillStyle = p.color;
  ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6); ctx.restore(); };
const nextFrame = (fn) => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 16));
const frame = () => {
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  particles = particles.filter((p) => p.alpha > 0);
  particles.forEach((p) => { step(p); draw(p); });
  if (particles.length > 0) nextFrame(frame);
  else { running = false; postMessage('done'); }
};
onmessage = (event) => {
  const message = event.data;
  if (message.type === 'canvas') { canvas = message.canvas; ctx = canvas.getContext('2d'); return; }
  if (message.type !== 'burst' || !ctx) return;
  for (let i = 0; i < message.count; i += 1) particles.push(make(message.x, message.y));
  if (!running) { running = true; nextFrame(frame); }
};
`;

function workerSupported(canvas: HTMLCanvasElement): boolean {
  return typeof Worker === 'function' && typeof URL?.createObjectURL === 'function'
    && typeof canvas.transferControlToOffscreen === 'function';
}

function spawnWorker(canvas: HTMLCanvasElement): Worker {
  const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
  const worker = new Worker(url);
  const offscreen = canvas.transferControlToOffscreen();

  URL.revokeObjectURL(url);
  worker.postMessage({ type: 'canvas', canvas: offscreen }, [offscreen]);

  return worker;
}

function startWorkerBurst(canvas: HTMLCanvasElement): WorkerBurst | null {
  try {
    const burst: WorkerBurst = { canvas, worker: spawnWorker(canvas) };

    const finish = () => {
      canvas.remove();
      burst.worker.terminate();
      if (workerBurst === burst) workerBurst = null;
    };

    burst.worker.onmessage = finish;
    burst.worker.onerror = finish;

    return burst;
  } catch {
    return null;
  }
}

/** `canvas` is the running burst's own canvas when one exists, so nothing is left over. */
function fireInWorker(cx: number, cy: number, canvas: HTMLCanvasElement): boolean {
  if (!workerBurst) {
    const burst = startWorkerBurst(canvas);

    if (!burst) return false;
    workerBurst = burst;
  }
  workerBurst.worker.postMessage({ type: 'burst', x: cx, y: cy, count: 80 });

  return true;
}

/* ── Read tracking for confetti dedup ─────────────────────────────── */

const _readIds = new Set<string>();
let _readLoaded = false;
let _projectId = '';
let _pendingSave: ReturnType<typeof setTimeout> | null = null;

function selectReadProject(projectId: string): void {
  if (_projectId !== projectId) _readIds.clear();
  _projectId = projectId;
}

/** Hydrate confetti state embedded in the AI overview bootstrap response. */
export function hydrateReadIds(projectId: string, messageIds: string[]): void {
  selectReadProject(projectId);
  messageIds.forEach((id) => _readIds.add(id));
  _readLoaded = true;
}

/** Load read message IDs from the backend. Call once on panel mount. */
export async function loadReadIds(projectId: string): Promise<void> {
  if (_readLoaded && _projectId === projectId) return;
  selectReadProject(projectId);

  try {
    // Dynamic import to avoid circular dependency
    const { projectService } = await import('../../services/projectService');
    const ids = await projectService.getReadMessageIds(projectId);

    if (_projectId === projectId) hydrateReadIds(projectId, ids);
  } catch { /* silent */ }
  if (_projectId === projectId) _readLoaded = true;
}

/** Check if a message has already shown confetti. */
export function isMessageRead(messageId: string): boolean {
  return _readIds.has(messageId);
}

/** Mark a message as read (confetti shown). Debounced save to backend. */
export function markMessageRead(messageId: string): void {
  _readIds.add(messageId);

  // Debounce: batch saves to avoid spamming the API
  if (_pendingSave) clearTimeout(_pendingSave);
  _pendingSave = setTimeout(async () => {
    if (!_projectId) return;

    try {
      const { projectService } = await import('../../services/projectService');

      await projectService.markMessagesRead(_projectId, Array.from(_readIds));
    } catch { /* silent */ }
  }, 2000);
}

/** Fire confetti only if this message hasn't been read yet. */
export function fireConfettiOnce(messageId: string, target?: HTMLElement): boolean {
  if (isMessageRead(messageId)) return false;

  markMessageRead(messageId);
  fireConfetti(target);

  return true;
}

/** A burst whose canvas left the document is over, whatever its loop believes. */
const liveBurst = <T extends { canvas: HTMLCanvasElement }>(burst: T | null): T | null => (
  burst?.canvas.isConnected ? burst : null
);

export function fireConfetti(target?: HTMLElement | ConfettiOrigin): void {
  mainThreadBurst = liveBurst(mainThreadBurst);
  workerBurst = liveBurst(workerBurst);
  const canvas = mainThreadBurst?.canvas ?? workerBurst?.canvas ?? createCanvas();
  const [cx, cy] = resolveOrigin(canvas, target);

  if (mainThreadBurst || !workerSupported(canvas) || !fireInWorker(cx, cy, canvas)) fireOnMainThread(cx, cy, canvas);
}
